import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@chakra-ui/react';

import { routes } from 'app/routing/routesConfig';
import { LoadingState } from 'types/enums';
import { Song as SongType } from 'types/song';
import {
  useFetchValidationSchema,
  useIsMounted,
  useSongRoomWebSocket,
} from 'app/utils/hooks';
import { useAppDispatch, useAppSelector } from 'app/store';
import { updateLastSeenMessageEffect } from 'app/store/Chat/effects';
import { fetchSongEffect } from 'app/store/SongList/effects';
import {
  createSongLoadingStateSelector,
  getSong,
  getSongUpdateLinkSelector,
} from 'app/store/SongList/selectors';

import { PageWrapper } from 'app/components/Core';
import { SongForm, SongHeaderBg, SongNavTabs } from 'app/components/Song';
import { SongLoader } from 'app/components/Song/SongLoader';
import { getSongChannelLoadingState } from 'app/store/Chat/selectors';
import { isLoading } from 'app/utils/utils';
import { setSongChatBarSettings } from 'app/store/Settings/slice';
import { getSongChatBarSettings } from 'app/store/Settings/selectors';
import { isDetailsPage, isLyricsPage } from './utils';
import { ReleaseCallback } from './ReleaseCallback';

export const Song = () => {
  const { guid } = useParams();
  const { state, pathname } = useLocation();
  const { shouldNotFetch } = state || {};
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const createSongLoadingState = useAppSelector(createSongLoadingStateSelector);
  const song = useAppSelector(getSong);
  const songUpdateLink = useAppSelector(getSongUpdateLinkSelector);
  const songChannelLoadingState = useAppSelector(getSongChannelLoadingState);

  const isSongChatOpen = useAppSelector(getSongChatBarSettings);
  const [isSongLoading, setIsSongLoading] = useState(!shouldNotFetch);
  const isCreateSongLoading = createSongLoadingState === LoadingState.FETCHING;

  useSongRoomWebSocket({ songGuid: guid as string });

  const { validationSchema, fetchingValidations } = useFetchValidationSchema({
    link: songUpdateLink,
    initialLoading: isSongLoading,
  });

  const loading = isSongLoading || isCreateSongLoading || fetchingValidations;

  const handleSongFetch = async () => {
    try {
      setIsSongLoading(true);
      await dispatch(fetchSongEffect({ guid: guid as string })).unwrap();
      if (isMounted()) {
        setIsSongLoading(false);
      }
    } catch (e: any) {
      if (isMounted()) {
        setIsSongLoading(false);
      }
      if (e?.statusCode === 404 || e?.statusCode === 403) {
        navigate(routes.pageNotFound.path, { replace: true });
      }
    }
  };

  const handleLastSeen = useCallback(() => {
    if (!isLoading(songChannelLoadingState) && isSongChatOpen) {
      dispatch(updateLastSeenMessageEffect());
    }
  }, [dispatch, songChannelLoadingState, isSongChatOpen]);

  useEffect(() => {
    return () => {
      dispatch(setSongChatBarSettings());
    };
  }, [dispatch]);

  useEffect(() => {
    if (shouldNotFetch) {
      navigate('', { state: { shouldNotFetch: undefined } });
    } else {
      handleSongFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guid]);

  useEffect(() => {
    handleLastSeen();
  }, [handleLastSeen]);

  if (loading) {
    return <SongLoader />;
  }

  if (!song?.guid) {
    navigate(routes.pageNotFound.path, { replace: true });
    return null;
  }

  return (
    <PageWrapper
      pt={0}
      px={0}
      height="auto"
      overflow="initial"
      flex={1}
      flexShrink={0}
      flexBasis="auto"
      pb={
        isDetailsPage(pathname, guid) || isLyricsPage(pathname, guid)
          ? 0
          : undefined
      }
    >
      <Box bg="surface1" zIndex={1101} position="relative">
        <SongHeaderBg status={song.status} />
        <SongForm validationSchema={validationSchema} />
        <SongNavTabs
          song={song as SongType}
          onChatOpen={() => {
            dispatch(setSongChatBarSettings({ songId: song.guid }));
          }}
        />
      </Box>
      <ReleaseCallback />
      <Outlet />
    </PageWrapper>
  );
};
